import { __rest, __assign } from 'tslib';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Select } from '@panwds/react-ui';
import { useFilters } from '../../hooks/useFilters/useFilters.esm.js';
import { Pill } from '../Pill/Pill.esm.js';
import { PillValue } from '../Pill/PillValue.esm.js';

var emptyItems = [];
/**
 * Provides a Select that will set the filter state for the name given
 * to this component.
 *
 * **Note**: This select is different from the SelectFilter in that it cannot be controlled
 * and expects _all_ of the items to be passed to it. It should be more performant with large
 * lists of items compared to SelectFilter.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { StaticSelectFilter } from "@panwds/react-filters";
 * ```
 */
function StaticSelectFilter(_a) {
    var autoFocus = _a.autoFocus, name = _a.name, _b = _a.items, items = _b === void 0 ? emptyItems : _b, label = _a.label, onClose = _a.onClose, _c = _a.disabled, disabled = _c === void 0 ? false : _c, rest = __rest(_a, ["autoFocus", "name", "items", "label", "onClose", "disabled"]);
    var intl = useIntl();
    var _d = useFilters(), filters = _d.filters, setFilters = _d.setFilters;
    var handleOnChange = useCallback(function (changes) {
        var _a, _b;
        if (rest.enableMultiSelect) {
            setFilters((_a = {},
                _a[name] = changes.selectedItems.map(function (item) { return item.value; }),
                _a));
        }
        else {
            setFilters((_b = {},
                _b[name] = "".concat(changes.selectedItem.value),
                _b));
        }
    }, [items, filters, setFilters, rest.enableMultiSelect]);
    var selectedItem = useMemo(function () {
        if (rest.enableMultiSelect) {
            return items.filter(function (item) {
                var values = filters[name];
                return Array.isArray(values) && values.includes(item.value);
            });
        }
        else {
            return items.find(function (item) {
                return item.value === filters[name];
            });
        }
    }, [items, filters[name], rest.enableMultiSelect]);
    var selectableItemsCount = useMemo(function () {
        if (rest.enableSelectAll) {
            return items.filter(function (item) { return !item.disabled; }).length;
        }
        else {
            return null;
        }
    }, [rest.enableSelectAll, items]);
    var displayLabel = useMemo(function () {
        var _a;
        var noItemSelected = !selectedItem || (Array.isArray(selectedItem) && !(selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.length));
        if (noItemSelected) {
            return intl.formatMessage({ id: "wU58a8", defaultMessage: [{ type: 0, value: "Select " }, { type: 1, value: "label" }] }, {
                label: label
            });
        }
        if (rest.enableSelectAll &&
            Array.isArray(selectedItem) &&
            selectedItem.length === selectableItemsCount) {
            return intl.formatMessage({ id: "0jcnlJ", defaultMessage: [{ type: 0, value: "All" }] });
        }
        if (Array.isArray(selectedItem)) {
            return selectedItem
                .map(function (item) { return String(item.children || item.value); })
                .join(", ");
        }
        return (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.children) !== null && _a !== void 0 ? _a : selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value;
    }, [rest.enableSelectAll, selectedItem, selectableItemsCount]);
    var select = (React.createElement(Select, __assign({}, rest, { disabled: disabled, items: items, button: React.createElement(PillValue, { label: displayLabel, disabled: disabled, autoFocus: autoFocus }), menuStyle: {
            marginTop: "0.5rem"
        }, onChange: handleOnChange, selectedItem: selectedItem })));
    return React.createElement(Pill, { label: label, onClose: onClose, select: select });
}

export { StaticSelectFilter };
//# sourceMappingURL=StaticSelectFilter.esm.js.map
